/* global subscribe, PUB_SUB_EVENTS, Shopify */

(function () {
  if (customElements.get('sticky-add-to-cart')) return;

  const debounce = (callback, delay = 150) => {
    let timeoutId;
    const debounced = (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback(...args), delay);
    };
    debounced.cancel = () => window.clearTimeout(timeoutId);
    return debounced;
  };

  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enabled !== 'true') return;
      this.init();

      if (window.Shopify && Shopify.designMode && !this.sectionLoadHandler) {
        this.sectionLoadHandler = this.handleSectionLoad.bind(this);
        document.addEventListener('shopify:section:load', this.sectionLoadHandler);
      }
    }

    handleSectionLoad(e) {
      if (e.target === this || e.target.contains(this)) {
        this.teardown();
        this.init();
      }
    }

    init() {
      this.mainForm = document.querySelector('product-form form');
      if (!this.mainForm || !this.mainForm.id) return;
      this.productForm = this.mainForm.closest('product-form');
      this.mainSubmitButton = this.productForm
        ? this.productForm.querySelector('[type="submit"]')
        : document.querySelector('[id^="ProductSubmitButton-"]');

      this.atcButton = this.querySelector('[data-sticky-atc-button]');
      this.dynamicCheckout = this.productForm ? this.productForm.querySelector('.shopify-payment-button') : null;
      this.dynamicCheckoutContext = this.dynamicCheckout
        ? this.dynamicCheckout.closest('.product__column-sticky')
        : null;
      this.dynamicCheckoutSlot = this.querySelector('[data-sticky-dynamic-checkout-slot]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.priceMainEl = this.querySelector('[data-sticky-price-main]');
      this.comparePriceEl = this.querySelector('[data-sticky-compare-price]');
      this.priceAddEl = this.querySelector('[data-sticky-price-add]');
      this.variantIdInput = this.mainForm.querySelector('[name="id"]');
      this.variantSelects = this.getCurrentVariantSelects();
      this.variantSummaryEl = this.querySelector('[data-sticky-variant-summary]');
      this.variantControlsEl = this.querySelector('[data-sticky-variant-controls]');
      this.quantityControl = this.querySelector('[data-sticky-quantity]');
      this.quantityValueEl = this.querySelector('[data-sticky-quantity-value]');
      this.urgencyEl = this.querySelector('[data-sticky-urgency]');
      this.urgencyTextEl = this.querySelector('[data-sticky-urgency-text]');
      this.variantInventoryData = this.getVariantInventoryData();
      this.ensureVariantSummary();
      this.variantOptionEls = this.variantSummaryEl
        ? Array.from(this.variantSummaryEl.querySelectorAll('[data-sticky-option-index]'))
        : [];
      this.renderVariantControls();

      this.bindAtcButton();
      this.bindSubmitStateSync();
      this.bindQuantitySync();
      this.bindDynamicCheckout();
      this.bindVisibilityTrigger();
      this.bindPriceSync();
      this.bindVariantSummarySync();
      this.bindHeightSync();
      this.recalculatePrice();
      this.syncVariantSummary();
      this.syncVariantControls();
      this.updateUrgency();
      this.bindMobileLayoutSync();
    }

    bindMobileLayoutSync() {
      this.mobileSingleRowMedia = window.matchMedia('(min-width: 360px) and (max-width: 749px)');
      this.mobileLayoutBreakpointHandler = () => this.scheduleMobileLayoutSync();
      this.mobileSingleRowMedia.addEventListener?.('change', this.mobileLayoutBreakpointHandler);

      this.handleMobileLayoutViewportChange = debounce(() => this.scheduleMobileLayoutSync(), 150);
      window.addEventListener('resize', this.handleMobileLayoutViewportChange, { passive: true });
      window.addEventListener('orientationchange', this.handleMobileLayoutViewportChange, { passive: true });

      this.syncMobileLayout();

      const fontReadyToken = {};
      this.mobileLayoutFontReadyToken = fontReadyToken;
      document.fonts?.ready.then(() => {
        if (this.mobileLayoutFontReadyToken === fontReadyToken && this.isConnected) {
          this.scheduleMobileLayoutSync();
        }
      });
    }

    scheduleMobileLayoutSync() {
      if (!this.mobileSingleRowMedia || this.mobileLayoutFrameId != null) return;
      this.mobileLayoutFrameId = window.requestAnimationFrame(() => {
        this.mobileLayoutFrameId = null;
        this.syncMobileLayout();
      });
    }

    syncMobileLayout() {
      const stackedClass = 'sticky-atc--mobile-stacked';
      const wasStacked = this.classList.contains(stackedClass);

      if (!this.mobileSingleRowMedia?.matches) {
        this.classList.remove(stackedClass);
        if (wasStacked) this.updateHeightVar?.();
        return false;
      }

      if (this.atcButton?.classList?.contains('is-busy')) return wasStacked;

      this.classList.remove(stackedClass);
      const shouldStack = this.hasMobileLayoutOverflow();
      this.classList.toggle(stackedClass, shouldStack);
      if (shouldStack !== wasStacked) this.updateHeightVar?.();
      return shouldStack;
    }

    hasMobileLayoutOverflow() {
      const valuesOverflow = Array.from(this.querySelectorAll('[data-sticky-option-visible-value]')).some(
        (valueEl) => valueEl.scrollWidth > valueEl.clientWidth + 1
      );
      if (valuesOverflow || !this.atcButton) return valuesOverflow;

      const textEl = this.atcButton.querySelector?.('.sticky-atc__button-text');
      if (!textEl) return this.atcButton.scrollWidth > this.atcButton.clientWidth + 1;

      const buttonStyle = window.getComputedStyle(this.atcButton);
      const horizontalPadding =
        (Number.parseFloat(buttonStyle.paddingLeft) || 0) + (Number.parseFloat(buttonStyle.paddingRight) || 0);
      const availableWidth = this.atcButton.clientWidth - horizontalPadding;
      const requiredWidth = Math.max(textEl.scrollWidth, textEl.getBoundingClientRect().width);
      return requiredWidth > availableWidth + 1;
    }

    bindHeightSync() {
      // Body padding-bottom adapts to the real bar height (which varies with
      // safe-area-inset-bottom on iPhone X+, optional thumbnail/title/price,
      // and tablet/desktop breakpoints). Falls back to fixed CSS values if
      // ResizeObserver is unavailable.
      this.updateHeightVar = () => {
        const stickyStyle = window.getComputedStyle(this);
        const bottomOffset = Number.parseFloat(stickyStyle.bottom) || 0;
        const h = this.offsetHeight + bottomOffset;
        if (h > 0) {
          document.documentElement.style.setProperty('--sticky-atc-height', h + 'px');
        }
        if (this.classList.contains('is-visible')) this.syncDynamicCheckoutPosition();
      };
      this.handleHeightViewportChange = debounce(this.updateHeightVar, 150);

      if (typeof ResizeObserver !== 'undefined') {
        this.heightObserver = new ResizeObserver(this.updateHeightVar);
        this.heightObserver.observe(this);
      }

      // Also react to viewport changes (orientation, breakpoint, URL bar collapse on iOS)
      this.updateHeightVar();
      window.addEventListener('resize', this.handleHeightViewportChange, { passive: true });
      window.addEventListener('orientationchange', this.handleHeightViewportChange, { passive: true });
    }

    bindAtcButton() {
      if (!this.atcButton) return;

      this.atcClickHandler = async (event) => {
        event.preventDefault();
        // busy 必须在 await 之前同步置上。原来的顺序是先 await 一个网络往返、
        // 之后才 setBusy —— 慢网下用户点完整整一个 RTT 看不到任何反应，
        // 于是再点一次。守卫读的是与 aria-busy 同步的 is-busy 类。
        if (this.atcButton.classList.contains('is-busy')) return;
        this.setBusy(this.atcButton, true);
        await this.syncMainVariantFromCurrentOptions();
        this.syncSubmitState();
        if (this.isMainSubmitDisabled()) {
          this.setBusy(this.atcButton, false);
          return;
        }
        this.submitMainForm();
      };
      this.atcButton.addEventListener('click', this.atcClickHandler);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.releaseBusyButtons();
        });
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
        this.cartErrorUnsub = subscribe(PUB_SUB_EVENTS.cartError, () => {
          this.releaseBusyButtons();
          window.requestAnimationFrame(() => {
            this.productForm
              ?.querySelector('.product-form__error-message-wrapper')
              ?.scrollIntoView({ block: 'center' });
          });
        });
      }

      this.bindModalCloseHandler();
    }

    submitMainForm() {
      if (this.mainForm && typeof this.mainForm.requestSubmit === 'function') {
        this.mainForm.requestSubmit(this.mainSubmitButton || undefined);
        return;
      }

      if (this.mainSubmitButton) {
        this.mainSubmitButton.click();
        return;
      }

      this.mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    bindDynamicCheckout() {
      if (!this.dynamicCheckoutSlot || !this.dynamicCheckout || this.dataset.showBuyNow !== 'true') {
        if (this.dynamicCheckoutSlot) this.dynamicCheckoutSlot.hidden = true;
        this.resetDynamicCheckoutPosition();
        return;
      }

      this.dynamicCheckoutSlot.hidden = false;
      this.dynamicCheckoutMedia = window.matchMedia('(min-width: 750px)');
      this.dynamicCheckoutBreakpointHandler = (event) => {
        if (!event.matches) {
          this.resetDynamicCheckoutPosition();
        } else if (this.classList.contains('is-visible')) {
          this.startDynamicCheckoutTracking();
        }
      };
      this.dynamicCheckoutMedia.addEventListener?.('change', this.dynamicCheckoutBreakpointHandler);
      this.handleDynamicCheckoutViewportChange = debounce(() => {
        if (this.classList.contains('is-visible')) {
          this.resetDynamicCheckoutPosition();
          this.startDynamicCheckoutTracking();
        } else {
          this.resetDynamicCheckoutPosition();
        }
      }, 150);
      window.addEventListener('resize', this.handleDynamicCheckoutViewportChange, { passive: true });
      window.addEventListener('orientationchange', this.handleDynamicCheckoutViewportChange, { passive: true });

      this.dynamicCheckoutTransitionHandler = (event) => {
        if (event.target !== this || event.propertyName !== 'transform') return;
        this.stopDynamicCheckoutTracking();
        this.syncDynamicCheckoutPosition();
      };
      this.addEventListener('transitionend', this.dynamicCheckoutTransitionHandler);
      this.addEventListener('transitioncancel', this.dynamicCheckoutTransitionHandler);

      if (typeof MutationObserver !== 'undefined') {
        this.dynamicCheckoutOverlayObserver = new MutationObserver(() => {
          if (this.isPageOverlayOpen()) {
            this.resetDynamicCheckoutPosition();
          } else if (this.classList.contains('is-visible')) {
            this.startDynamicCheckoutTracking();
          }
        });
        this.dynamicCheckoutOverlayObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class'],
        });
        this.dynamicCheckoutOverlayObserver.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
      this.syncDynamicCheckoutPosition();
    }

    isPageOverlayOpen() {
      const bodyClasses = document.body?.classList;
      const rootClasses = document.documentElement?.classList;
      const bodyHasOverflowLock = bodyClasses
        ? Array.from(bodyClasses).some((className) => className.startsWith('overflow-hidden'))
        : false;

      return Boolean(
        bodyHasOverflowLock ||
          bodyClasses?.contains('menu-drawer-open') ||
          bodyClasses?.contains('protection-modal-open') ||
          rootClasses?.contains('menu-drawer-open') ||
          rootClasses?.contains('protection-modal-open')
      );
    }

    canProjectDynamicCheckout() {
      if (!this.dynamicCheckout || !this.dynamicCheckoutSlot) return false;
      if (this.dataset.showBuyNow !== 'true' || !this.classList.contains('is-visible')) return false;
      const isTabletOrDesktop = this.dynamicCheckoutMedia
        ? this.dynamicCheckoutMedia.matches
        : window.matchMedia('(min-width: 750px)').matches;
      if (!isTabletOrDesktop) return false;
      if (this.isPageOverlayOpen()) return false;

      const stickyStyle = window.getComputedStyle(this);
      const slotStyle = window.getComputedStyle(this.dynamicCheckoutSlot);
      const checkoutStyle = window.getComputedStyle(this.dynamicCheckout);
      return Boolean(
        stickyStyle.display !== 'none' &&
          stickyStyle.visibility !== 'hidden' &&
          slotStyle.display !== 'none' &&
          slotStyle.visibility !== 'hidden' &&
          checkoutStyle.display !== 'none' &&
          checkoutStyle.visibility !== 'hidden' &&
          this.dynamicCheckout.getClientRects().length
      );
    }

    createDynamicCheckoutSourcePlaceholder() {
      if (this.dynamicCheckoutSourcePlaceholder?.isConnected) return true;
      const parent = this.dynamicCheckout?.parentNode;
      if (!parent) return false;

      const sourceRect = this.dynamicCheckout.getBoundingClientRect();
      if (sourceRect.width <= 0 || sourceRect.height <= 0) return false;

      const sourceStyle = window.getComputedStyle(this.dynamicCheckout);
      const placeholder = document.createElement('div');
      placeholder.className = 'sticky-atc__dynamic-source-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.style.boxSizing = 'border-box';
      placeholder.style.width = `${sourceRect.width}px`;
      placeholder.style.maxWidth = '100%';
      placeholder.style.height = `${sourceRect.height}px`;
      placeholder.style.marginTop = sourceStyle.marginTop;
      placeholder.style.marginRight = sourceStyle.marginRight;
      placeholder.style.marginBottom = sourceStyle.marginBottom;
      placeholder.style.marginLeft = sourceStyle.marginLeft;
      placeholder.style.order = sourceStyle.order;
      placeholder.style.gridColumnStart = sourceStyle.gridColumnStart;
      placeholder.style.gridColumnEnd = sourceStyle.gridColumnEnd;
      placeholder.style.gridRowStart = sourceStyle.gridRowStart;
      placeholder.style.gridRowEnd = sourceStyle.gridRowEnd;
      placeholder.style.alignSelf = sourceStyle.alignSelf;
      placeholder.style.justifySelf = sourceStyle.justifySelf;
      placeholder.style.flexGrow = sourceStyle.flexGrow;
      placeholder.style.flexShrink = sourceStyle.flexShrink;
      placeholder.style.flexBasis = sourceStyle.flexBasis;
      placeholder.style.visibility = 'hidden';
      placeholder.style.pointerEvents = 'none';
      parent.insertBefore(placeholder, this.dynamicCheckout.nextSibling);
      this.dynamicCheckoutSourcePlaceholder = placeholder;
      return true;
    }

    captureDynamicCheckoutInlineStyles() {
      if (this.dynamicCheckoutInlineStyles || !this.dynamicCheckout) return;
      const properties = [
        'position',
        'left',
        'top',
        'width',
        'height',
        'margin-top',
        'margin-right',
        'margin-bottom',
        'margin-left',
      ];
      this.dynamicCheckoutInlineStyles = properties.map((property) => ({
        property,
        value: this.dynamicCheckout.style.getPropertyValue(property),
        priority: this.dynamicCheckout.style.getPropertyPriority(property),
      }));
    }

    restoreDynamicCheckoutInlineStyles() {
      if (!this.dynamicCheckout || !this.dynamicCheckoutInlineStyles) return;
      this.dynamicCheckoutInlineStyles.forEach(({ property, value, priority }) => {
        if (value) {
          this.dynamicCheckout.style.setProperty(property, value, priority);
        } else {
          this.dynamicCheckout.style.removeProperty(property);
        }
      });
      this.dynamicCheckoutInlineStyles = null;
    }

    removeDynamicCheckoutSourcePlaceholder() {
      this.dynamicCheckoutSourcePlaceholder?.remove();
      this.dynamicCheckoutSourcePlaceholder = null;
    }

    syncDynamicCheckoutPosition() {
      if (!this.canProjectDynamicCheckout()) {
        this.resetDynamicCheckoutPosition();
        return false;
      }

      const slotRect = this.dynamicCheckoutSlot.getBoundingClientRect();
      if (slotRect.width <= 0 || slotRect.height <= 0) {
        this.resetDynamicCheckoutPosition();
        return false;
      }

      if (!this.createDynamicCheckoutSourcePlaceholder()) {
        this.resetDynamicCheckoutPosition();
        return false;
      }
      this.captureDynamicCheckoutInlineStyles();
      this.dynamicCheckout.classList.add('is-sticky-dynamic-checkout');
      this.dynamicCheckoutContext?.classList.add('sticky-atc__dynamic-context--active');
      this.dynamicCheckout.style.position = 'fixed';
      this.dynamicCheckout.style.left = `${slotRect.left}px`;
      this.dynamicCheckout.style.top = `${slotRect.top}px`;
      this.dynamicCheckout.style.width = `${slotRect.width}px`;
      this.dynamicCheckout.style.height = `${slotRect.height}px`;
      this.dynamicCheckout.style.setProperty('margin-top', '0');
      this.dynamicCheckout.style.setProperty('margin-right', '0');
      this.dynamicCheckout.style.setProperty('margin-bottom', '0');
      this.dynamicCheckout.style.setProperty('margin-left', '0');
      return true;
    }

    startDynamicCheckoutTracking() {
      this.stopDynamicCheckoutTracking();
      if (!this.canProjectDynamicCheckout()) {
        this.resetDynamicCheckoutPosition();
        return;
      }

      const trackingDeadline = performance.now() + 450;
      const track = () => {
        this.dynamicCheckoutFrame = null;
        if (!this.canProjectDynamicCheckout()) {
          this.resetDynamicCheckoutPosition();
          return;
        }

        if (this.syncDynamicCheckoutPosition() && performance.now() < trackingDeadline) {
          this.dynamicCheckoutFrame = window.requestAnimationFrame(track);
        }
      };
      this.dynamicCheckoutFrame = window.requestAnimationFrame(track);
    }

    stopDynamicCheckoutTracking() {
      if (this.dynamicCheckoutFrame == null) return;
      window.cancelAnimationFrame(this.dynamicCheckoutFrame);
      this.dynamicCheckoutFrame = null;
    }

    resetDynamicCheckoutPosition() {
      this.stopDynamicCheckoutTracking();
      if (!this.dynamicCheckout) {
        this.dynamicCheckoutInlineStyles = null;
        this.removeDynamicCheckoutSourcePlaceholder();
        return;
      }

      this.dynamicCheckout.classList.remove('is-sticky-dynamic-checkout');
      this.dynamicCheckoutContext?.classList.remove('sticky-atc__dynamic-context--active');
      this.restoreDynamicCheckoutInlineStyles();
      this.removeDynamicCheckoutSourcePlaceholder();
    }

    bindSubmitStateSync() {
      this.syncSubmitState();

      if (this.variantIdInput) {
        this.boundVariantIdInput = this.variantIdInput;
        this.variantChangeHandler = () => {
          this.syncSubmitState();
          this.updateUrgency();
          window.requestAnimationFrame(() => this.refreshMainQuantityInput());
        };
        this.boundVariantIdInput.addEventListener('change', this.variantChangeHandler);
      }

      if (!this.mainSubmitButton || typeof MutationObserver === 'undefined') return;

      this.submitStateObserver = new MutationObserver(() => this.syncSubmitState());
      this.submitStateObserver.observe(this.mainSubmitButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    syncSubmitState() {
      const mainDisabled = this.isMainSubmitDisabled();
      const mainText = this.getMainSubmitText();

      if (this.atcButton) {
        this.applyButtonState(this.atcButton, mainDisabled);
        if (mainText) {
          const textEl = this.atcButton.querySelector('.sticky-atc__button-text');
          if (textEl) textEl.textContent = mainText;
          this.atcButton.setAttribute('aria-label', mainText);
        }
      }
      this.scheduleMobileLayoutSync();
    }

    getMainQuantityInput() {
      const formQuantity = this.mainForm?.elements?.namedItem('quantity');
      if (formQuantity instanceof HTMLInputElement) return formQuantity;
      return document.querySelector('quantity-input input[name="quantity"]');
    }

    bindQuantitySync() {
      if (!this.quantityControl || !this.quantityValueEl) return;

      this.mainQuantityChangeHandler = () => this.syncQuantity();
      this.refreshMainQuantityInput();
      if (!this.mainQuantityInput) {
        this.quantityControl.hidden = true;
        return;
      }

      this.stickyQuantityClickHandler = (event) => {
        const button = event.target.closest('[data-sticky-quantity-action]');
        if (!button || !this.quantityControl.contains(button) || button.disabled) return;

        event.preventDefault();
        this.refreshMainQuantityInput();
        if (!this.mainQuantityInput) return;

        const action = button.dataset.stickyQuantityAction;
        const nativeQuantity = this.mainQuantityInput.closest('quantity-input');
        const nativeButton = nativeQuantity?.querySelector(`button[name="${action}"]`);
        if (nativeButton) {
          nativeButton.click();
          return;
        }

        const previousValue = this.mainQuantityInput.value;
        if (action === 'plus') this.mainQuantityInput.stepUp();
        if (action === 'minus') this.mainQuantityInput.stepDown();
        if (previousValue !== this.mainQuantityInput.value) {
          this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      this.quantityControl.addEventListener('click', this.stickyQuantityClickHandler);
      this.syncQuantity();
    }

    refreshMainQuantityInput() {
      const nextInput = this.getMainQuantityInput();
      if (nextInput === this.mainQuantityInput) {
        this.syncQuantity();
        return;
      }

      if (this.mainQuantityInput && this.mainQuantityChangeHandler) {
        this.mainQuantityInput.removeEventListener('change', this.mainQuantityChangeHandler);
        this.mainQuantityInput = null;
      }
      this.mainQuantityInput = nextInput;
      this.quantityControl.hidden = !this.mainQuantityInput;
      if (this.mainQuantityInput && this.mainQuantityChangeHandler) {
        this.mainQuantityInput.addEventListener('change', this.mainQuantityChangeHandler);
      }
      this.syncQuantity();
    }

    syncQuantity() {
      if (!this.mainQuantityInput || !this.quantityControl || !this.quantityValueEl) return;

      const value = Number.parseInt(this.mainQuantityInput.value, 10);
      const min = Number.parseInt(this.mainQuantityInput.min, 10);
      const max = Number.parseInt(this.mainQuantityInput.max, 10);
      const nativeQuantity = this.mainQuantityInput.closest('quantity-input');
      const nativeMinus = nativeQuantity?.querySelector('button[name="minus"]');
      const nativePlus = nativeQuantity?.querySelector('button[name="plus"]');

      this.quantityValueEl.textContent = Number.isFinite(value) ? String(value) : this.mainQuantityInput.value;
      this.quantityControl.querySelectorAll('[data-sticky-quantity-action]').forEach((button) => {
        const action = button.dataset.stickyQuantityAction;
        const reachedMin = action === 'minus' && Number.isFinite(min) && value <= min;
        const reachedMax = action === 'plus' && Number.isFinite(max) && value >= max;
        const nativeDisabled =
          action === 'minus'
            ? nativeMinus?.disabled || nativeMinus?.classList.contains('disabled')
            : nativePlus?.disabled || nativePlus?.classList.contains('disabled');
        button.disabled = Boolean(reachedMin || reachedMax || nativeDisabled);
      });
    }

    getVariantInventoryData() {
      const dataEl = this.querySelector('[data-sticky-variant-data]');
      if (!dataEl) return [];

      try {
        const data = JSON.parse(dataEl.textContent);
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    }

    updateUrgency() {
      if (!this.urgencyEl) return;

      this.variantIdInput = this.mainForm?.querySelector('[name="id"]') || this.variantIdInput;
      const threshold = Number.parseInt(this.dataset.inventoryThreshold, 10);
      const variantId = this.variantIdInput?.value;
      const variant = this.variantInventoryData.find((item) => String(item.id) === String(variantId));
      const inventoryQuantity = Number(variant?.inventory_quantity);
      const shouldShow = Boolean(
        this.dataset.showInventoryUrgency === 'true' &&
          variant &&
          variant.available &&
          variant.inventory_management === 'shopify' &&
          variant.inventory_policy !== 'continue' &&
          Number.isFinite(inventoryQuantity) &&
          inventoryQuantity > 0 &&
          Number.isFinite(threshold) &&
          inventoryQuantity <= threshold
      );

      this.urgencyEl.hidden = !shouldShow;
      if (shouldShow && this.urgencyTextEl) {
        const inventoryText = (this.dataset.urgencyTemplate || '').replace(
          '__quantity__',
          String(inventoryQuantity)
        );
        this.urgencyTextEl.textContent = [inventoryText, this.dataset.urgencyDispatch].filter(Boolean).join(' - ');
      }
    }

    applyButtonState(button, mainDisabled) {
      if (!button) return;
      const isBusy = button.getAttribute('aria-busy') === 'true';
      const isDisabled = mainDisabled || isBusy;

      button.disabled = isDisabled;
      if (isDisabled) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    }

    isMainSubmitDisabled() {
      const hasVariantId = this.variantIdInput ? this.variantIdInput.value !== '' : true;
      return Boolean(
        !hasVariantId ||
          (this.mainSubmitButton &&
            (this.mainSubmitButton.disabled || this.mainSubmitButton.getAttribute('aria-disabled') === 'true'))
      );
    }

    getMainSubmitText() {
      const textEl = this.mainSubmitButton ? this.mainSubmitButton.querySelector('.product-form__submit-text') : null;
      const text = textEl ? textEl.textContent.trim().replace(/\s+/g, ' ') : '';
      return text || (window.variantStrings && window.variantStrings.addToCart) || '';
    }

    bindVariantSummarySync() {
      if (!this.variantOptionEls.length && !this.variantControlsEl) return;

      this.variantSummaryChangeHandler = (event) => {
        if (event.target && event.target.closest && event.target.closest('variant-selects')) {
          this.variantSelects = this.getCurrentVariantSelects();
          this.ensureVariantSummary();
          this.variantOptionEls = this.variantSummaryEl
            ? Array.from(this.variantSummaryEl.querySelectorAll('[data-sticky-option-index]'))
            : [];
          this.renderVariantControls();
          this.syncVariantSummary();
          this.syncVariantControls();
          this.updateUrgency();
        }
      };
      document.addEventListener('change', this.variantSummaryChangeHandler);

      if (this.variantControlsEl) {
        this.stickyVariantControlClickHandler = (event) => {
          const trigger = event.target && event.target.closest('[data-sticky-option-trigger]');
          const choice = event.target && event.target.closest('[data-sticky-option-choice]');

          if (trigger && this.variantControlsEl.contains(trigger)) {
            event.preventDefault();
            this.toggleStickyVariantMenu(trigger);
            return;
          }

          if (choice && this.variantControlsEl.contains(choice)) {
            event.preventDefault();
            this.handleStickyVariantChoice(choice);
          }
        };
        this.variantControlsEl.addEventListener('click', this.stickyVariantControlClickHandler);

        this.stickyVariantDocumentClickHandler = (event) => {
          if (!this.variantControlsEl.contains(event.target)) this.closeStickyVariantMenus();
        };
        document.addEventListener('click', this.stickyVariantDocumentClickHandler);

        this.stickyVariantKeydownHandler = (event) => {
          if (event.key === 'Escape') this.closeStickyVariantMenus(true);
        };
        document.addEventListener('keydown', this.stickyVariantKeydownHandler);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.variantSummaryUnsub = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          this.variantSelects = this.getCurrentVariantSelects();
          this.ensureVariantSummary();
          this.variantOptionEls = this.variantSummaryEl
            ? Array.from(this.variantSummaryEl.querySelectorAll('[data-sticky-option-index]'))
            : [];
          this.renderVariantControls();
          this.syncVariantSummary();
          this.syncVariantControls();
          this.updateUrgency();
          window.requestAnimationFrame(() => this.refreshMainQuantityInput());
        });
      }
    }

    getCurrentVariantSelects() {
      return document.querySelector('variant-selects');
    }

    ensureVariantSummary() {
      this.variantSelects = this.getCurrentVariantSelects();
      if (!this.variantSelects) return;

      if (!this.variantSummaryEl) {
        const infoEl = this.querySelector('.sticky-atc__info');
        if (!infoEl) return;

        this.variantSummaryEl = document.createElement('div');
        this.variantSummaryEl.className = 'sticky-atc__variant-summary';
        this.variantSummaryEl.dataset.stickyVariantSummary = '';
        infoEl.appendChild(this.variantSummaryEl);
      }

      if (this.variantSummaryEl.querySelector('[data-sticky-option-index]')) return;

      this.getStickyVariantGroups().forEach(({ index, optionName }) => {
        const optionEl = document.createElement('span');
        optionEl.className = 'sticky-atc__variant-option';
        optionEl.dataset.stickyOptionIndex = String(index);
        optionEl.dataset.stickyOptionName = optionName;
        if (this.isColorOption(optionName)) {
          optionEl.classList.add('sticky-atc__variant-option--color');
        }

        const labelEl = document.createElement('span');
        labelEl.className = 'sticky-atc__variant-label';
        labelEl.textContent = optionName;

        const valueRowEl = document.createElement('span');
        valueRowEl.className = 'sticky-atc__variant-value-row';

        const swatchEl = document.createElement('span');
        swatchEl.className = 'sticky-atc__variant-summary-swatch';
        swatchEl.dataset.stickySummarySwatch = '';
        swatchEl.hidden = true;

        const valueEl = document.createElement('span');
        valueEl.className = 'sticky-atc__variant-value';
        valueEl.dataset.stickyOptionValue = '';

        valueRowEl.append(swatchEl, valueEl);
        optionEl.append(labelEl, valueRowEl);
        this.variantSummaryEl.appendChild(optionEl);
      });
    }

    getVariantOptionName(inputGroup, index) {
      const labelEl = inputGroup.querySelector('.form__label, legend, label');
      const labelClone = labelEl ? labelEl.cloneNode(true) : null;
      labelClone?.querySelectorAll('[data-selected-value], .label-unavailable').forEach((el) => el.remove());
      const labelText = labelClone ? labelClone.textContent.trim().replace(/\s+/g, ' ') : '';
      const inputName = inputGroup.querySelector('input[type="radio"], select')?.name || '';
      const fallback = inputName.match(/options\[(.*)\]/)?.[1] || inputName || 'Option ' + (index + 1);

      return (labelText || fallback).replace(/\s*:\s*.*/, '').replace(/:$/, '');
    }

    shouldShowVariantOption(optionName) {
      const normalized = optionName.trim().toLowerCase();
      return normalized === 'color' || normalized === 'colour' || normalized === 'size';
    }

    isColorOption(optionName) {
      const normalized = optionName.trim().toLowerCase();
      return normalized === 'color' || normalized === 'colour';
    }

    getStickyVariantGroups() {
      this.variantSelects = this.getCurrentVariantSelects();
      if (!this.variantSelects) return [];

      return Array.from(this.variantSelects.querySelectorAll('.product-form__input'))
        .map((inputGroup, index) => ({
          index,
          inputGroup,
          optionName: this.getVariantOptionName(inputGroup, index),
          values: this.getVariantOptionValues(inputGroup),
        }))
        .filter((group) => this.shouldShowVariantOption(group.optionName) && group.values.length);
    }

    getVariantOptionValues(inputGroup) {
      const select = inputGroup.querySelector('select');
      if (select) {
        return Array.from(select.options)
          .filter((option) => option.dataset.variantOptionHidden !== 'true')
          .map((option) => ({
            value: option.value,
            label: option.textContent.trim().replace(/\s+/g, ' '),
            selected: option.selected,
            // V5 P2.6 — main variant picker marks sold-out values with a
            // CSS class instead of the `disabled` attribute (see
            // snippets/product-variant-options.liquid and swatch-input).
            // Read both so sticky ATC mirrors the main picker's sold-out
            // state instead of showing every variant as available (#11).
            disabled:
              option.disabled ||
              option.classList.contains('disabled') ||
              option.classList.contains('visually-disabled'),
          }));
      }

      return Array.from(inputGroup.querySelectorAll('input[type="radio"]'))
        .filter((input) => input.dataset.variantOptionHidden !== 'true')
        .map((input) => ({
          value: input.value,
          label: input.value,
          selected: input.checked,
          disabled:
            input.disabled ||
            input.classList.contains('disabled') ||
            input.classList.contains('visually-disabled') ||
            // Some swatch markups put the disabled class on the wrapping label
            // (e.g. .swatch-input__input + label.disabled). Walk to the label to
            // catch it.
            (input.id &&
              inputGroup.querySelector('label[for="' + input.id + '"]')?.classList.contains('disabled')),
        }));
    }

    cloneIconTemplate(selector) {
      const template = this.querySelector(selector);
      const icon = template?.content?.firstElementChild?.cloneNode(true) || null;
      icon?.querySelectorAll('svg').forEach((svg) => svg.setAttribute('aria-hidden', 'true'));
      return icon;
    }

    renderVariantControls() {
      if (!this.variantControlsEl) return;

      const groups = this.getStickyVariantGroups();
      this.variantControlsEl.replaceChildren(
        ...groups.map((group) => {
          const controlEl = document.createElement('div');
          controlEl.className = 'sticky-atc__variant-control';
          if (this.isColorOption(group.optionName)) {
            controlEl.classList.add('sticky-atc__variant-control--color');
          }
          controlEl.dataset.stickyOptionControl = '';
          controlEl.dataset.stickyOptionIndex = String(group.index);
          controlEl.dataset.stickyOptionName = group.optionName;

          const triggerEl = document.createElement('button');
          triggerEl.type = 'button';
          triggerEl.id = `StickyOption-${group.index}`;
          triggerEl.className = 'sticky-atc__variant-trigger';
          triggerEl.dataset.stickyOptionTrigger = '';
          triggerEl.dataset.stickyOptionIndex = String(group.index);
          triggerEl.setAttribute('aria-haspopup', 'listbox');
          triggerEl.setAttribute('aria-expanded', 'false');
          triggerEl.setAttribute('aria-controls', `StickyOptionMenu-${group.index}`);

          const labelEl = document.createElement('span');
          labelEl.className = 'sticky-atc__variant-control-label';
          labelEl.textContent = group.optionName;

          const selectedValue = group.values.find((value) => value.selected) || group.values[0];
          const swatchEl = document.createElement('span');
          swatchEl.className = 'sticky-atc__variant-control-swatch';
          swatchEl.dataset.stickyOptionSwatch = '';

          const valueEl = document.createElement('span');
          valueEl.className = 'sticky-atc__variant-control-value';
          valueEl.dataset.stickyOptionVisibleValue = '';
          valueEl.textContent = selectedValue ? selectedValue.label : '';

          const valueRowEl = document.createElement('span');
          valueRowEl.className = 'sticky-atc__variant-control-value-row';

          this.syncStickySwatch(
            swatchEl,
            group.optionName,
            selectedValue ? selectedValue.value : '',
            group.inputGroup
          );

          valueRowEl.append(swatchEl, valueEl);
          const caretEl = this.cloneIconTemplate('[data-sticky-caret-template]');
          triggerEl.append(labelEl, valueRowEl);
          if (caretEl) triggerEl.appendChild(caretEl);

          const menuEl = document.createElement('div');
          menuEl.id = `StickyOptionMenu-${group.index}`;
          menuEl.className = 'sticky-atc__variant-menu';
          menuEl.dataset.stickyOptionMenu = '';
          menuEl.setAttribute('role', 'listbox');
          menuEl.hidden = true;

          const menuValues = group.values;
          const enabledAlternatives = menuValues.filter((value) => !value.selected && !value.disabled);
          triggerEl.disabled = !enabledAlternatives.length;
          triggerEl.setAttribute('aria-disabled', enabledAlternatives.length ? 'false' : 'true');

          menuValues.forEach((value) => {
            const optionEl = document.createElement('button');
            optionEl.type = 'button';
            optionEl.className = 'sticky-atc__variant-menu-option';
            optionEl.dataset.stickyOptionChoice = '';
            optionEl.dataset.stickyOptionIndex = String(group.index);
            optionEl.dataset.stickyOptionValue = value.value;
            optionEl.dataset.stickyOptionLabel = value.label;
            optionEl.setAttribute('role', 'option');
            optionEl.setAttribute('aria-selected', value.selected ? 'true' : 'false');
            optionEl.disabled = value.disabled;

            const optionSwatchEl = document.createElement('span');
            optionSwatchEl.className = 'sticky-atc__variant-menu-swatch';
            this.syncStickySwatch(optionSwatchEl, group.optionName, value.value, group.inputGroup);

            const optionTextEl = document.createElement('span');
            optionTextEl.className = 'sticky-atc__variant-menu-option-text';
            optionTextEl.textContent = value.label;

            const checkEl = this.cloneIconTemplate('[data-sticky-check-template]');
            optionEl.append(optionSwatchEl, optionTextEl);
            if (checkEl) optionEl.appendChild(checkEl);
            menuEl.appendChild(optionEl);
          });

          controlEl.append(triggerEl, menuEl);
          return controlEl;
        })
      );
      this.scheduleMobileLayoutSync();
    }

    syncVariantControls() {
      if (!this.variantControlsEl) return;

      const optionInputs = Array.from(this.getCurrentVariantSelects()?.querySelectorAll('.product-form__input') || []);
      this.variantControlsEl.querySelectorAll('[data-sticky-option-control]').forEach((controlEl) => {
        const optionIndex = Number(controlEl.dataset.stickyOptionIndex);
        const inputGroup = optionInputs[optionIndex];
        const value = this.getSelectedVariantOptionValue(inputGroup);
        if (!value) return;
        this.syncStickyControlDisplay(controlEl, value, inputGroup);
      });
      this.scheduleMobileLayoutSync();
    }

    syncStickyControlDisplay(controlEl, value, inputGroup) {
      if (!controlEl) return;
      const valueEl = controlEl.querySelector('[data-sticky-option-visible-value]');
      const choiceEl = Array.from(controlEl.querySelectorAll('[data-sticky-option-choice]')).find(
        (choice) => choice.dataset.stickyOptionValue === value
      );
      const label = choiceEl ? choiceEl.dataset.stickyOptionLabel : value;

      if (valueEl) valueEl.textContent = label;
      this.syncStickySwatch(
        controlEl.querySelector('[data-sticky-option-swatch]'),
        controlEl.dataset.stickyOptionName,
        value,
        inputGroup
      );

      controlEl.querySelectorAll('[data-sticky-option-choice]').forEach((choice) => {
        choice.setAttribute('aria-selected', choice.dataset.stickyOptionValue === value ? 'true' : 'false');
      });
    }

    syncStickySwatch(swatchEl, optionName, value, inputGroup) {
      if (!swatchEl) return;
      const swatchValue = this.getStickySwatchValue(optionName, value, inputGroup);
      swatchEl.hidden = !swatchValue;
      if (swatchValue) {
        swatchEl.style.setProperty('--sticky-option-swatch', swatchValue);
      } else {
        swatchEl.style.removeProperty('--sticky-option-swatch');
      }
    }

    getStickySwatchValue(optionName, value, inputGroup) {
      if (!optionName || !value || !this.isColorOption(optionName)) return '';

      const resolvedGroup = inputGroup || this.getStickyVariantGroups().find((group) => group.optionName === optionName)?.inputGroup;
      const selectOption = Array.from(resolvedGroup?.querySelectorAll('select option') || []).find(
        (option) => option.value === value
      );
      if (selectOption?.dataset.optionSwatchValue) return selectOption.dataset.optionSwatchValue;

      const radioInput = Array.from(resolvedGroup?.querySelectorAll('input[type="radio"]') || []).find(
        (input) => input.value === value
      );
      const labelEl = radioInput?.id ? resolvedGroup.querySelector(`label[for="${radioInput.id}"]`) : null;
      const swatchEl = labelEl?.querySelector('.swatch');
      const swatchBackground = swatchEl?.style.getPropertyValue('--swatch--background');
      if (swatchBackground) return swatchBackground;

      const variantBackground = labelEl?.style.getPropertyValue('--variant-option-bg');
      if (variantBackground) return variantBackground;

      const normalizedValue = value.trim().toLowerCase();
      if (normalizedValue.includes('black')) return 'var(--r3-neutral-900)';
      if (normalizedValue.includes('red')) return 'var(--r3-color-cta)';
      if (normalizedValue.includes('white')) return 'var(--r3-neutral-0)';
      if (normalizedValue.includes('gold')) return 'var(--r3-color-secondary)';
      return '';
    }

    getSelectedVariantOptionValue(inputGroup) {
      if (!inputGroup) return '';
      const checkedInput = inputGroup.querySelector('input[type="radio"]:checked');
      const selectedOption = inputGroup.querySelector('select option:checked');
      return checkedInput ? checkedInput.value : selectedOption ? selectedOption.value : '';
    }

    toggleStickyVariantMenu(triggerEl) {
      const controlEl = triggerEl.closest('[data-sticky-option-control]');
      const menuEl = controlEl && controlEl.querySelector('[data-sticky-option-menu]');
      if (!menuEl) return;

      const shouldOpen = menuEl.hidden;
      this.closeStickyVariantMenus();
      menuEl.hidden = !shouldOpen;
      triggerEl.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      controlEl.classList.toggle('is-open', shouldOpen);
      if (shouldOpen) {
        window.requestAnimationFrame(() => {
          const selectedOption = menuEl.querySelector('[aria-selected="true"]:not(:disabled)');
          const firstOption = menuEl.querySelector('[data-sticky-option-choice]:not(:disabled)');
          (selectedOption || firstOption)?.focus();
        });
      }
    }

    closeStickyVariantMenus(restoreFocus = false) {
      if (!this.variantControlsEl) return;
      const activeElement = document.activeElement;
      let triggerToFocus = null;
      this.variantControlsEl.querySelectorAll('[data-sticky-option-menu]').forEach((menuEl) => {
        const controlEl = menuEl.closest('[data-sticky-option-control]');
        if (!triggerToFocus && (menuEl.contains(activeElement) || (restoreFocus && !menuEl.hidden))) {
          triggerToFocus = controlEl?.querySelector('[data-sticky-option-trigger]');
        }
        menuEl.hidden = true;
      });
      this.variantControlsEl.querySelectorAll('[data-sticky-option-trigger]').forEach((triggerEl) => {
        triggerEl.setAttribute('aria-expanded', 'false');
      });
      this.variantControlsEl.querySelectorAll('[data-sticky-option-control]').forEach((controlEl) => {
        controlEl.classList.remove('is-open');
      });
      triggerToFocus?.focus();
    }

    async handleStickyVariantChoice(choiceEl) {
      if (choiceEl.disabled) return;
      const optionIndex = Number(choiceEl.dataset.stickyOptionIndex);
      const optionInputs = Array.from(this.getCurrentVariantSelects()?.querySelectorAll('.product-form__input') || []);
      if (!optionInputs[optionIndex]) return;

      if (choiceEl.getAttribute('aria-selected') === 'true') {
        this.closeStickyVariantMenus();
        this.variantControlsEl.querySelector(`#StickyOption-${optionIndex}`)?.focus();
        return;
      }

      const selectedOptions = optionInputs.map((inputGroup) => this.getSelectedVariantOptionValue(inputGroup));
      selectedOptions[optionIndex] = choiceEl.dataset.stickyOptionValue;
      this.closeStickyVariantMenus();

      const availableVariant = await this.getBestAvailableVariant(selectedOptions, optionIndex);
      const optionValues = availableVariant ? this.getVariantOptions(availableVariant) : selectedOptions;
      this.applyNativeOptionValues(optionValues, optionIndex);
      await this.syncMainVariantFromCurrentOptions();

      this.renderVariantControls();
      this.syncVariantSummary();
      this.syncVariantControls();
      this.variantControlsEl.querySelector(`#StickyOption-${optionIndex}`)?.focus();
    }

    applyNativeOptionValues(optionValues, preferredEventIndex) {
      const variantSelects = this.getCurrentVariantSelects();
      const optionInputs = Array.from(variantSelects?.querySelectorAll('.product-form__input') || []);
      let eventTarget = null;

      optionInputs.forEach((inputGroup, index) => {
        const nextValue = optionValues[index];
        if (!nextValue) return;

        const nativeSelect = inputGroup.querySelector('select');
        if (nativeSelect) {
          if (nativeSelect.value !== nextValue) {
            nativeSelect.value = nextValue;
          }
          if (index === preferredEventIndex) eventTarget = nativeSelect;
          return;
        }

        const radioInput = Array.from(inputGroup.querySelectorAll('input[type="radio"]')).find(
          (input) => input.value === nextValue
        );
        if (!radioInput) return;
        radioInput.checked = true;
        if (index === preferredEventIndex) eventTarget = radioInput;
      });

      eventTarget = eventTarget || optionInputs[preferredEventIndex]?.querySelector('input[type="radio"], select');
      if (eventTarget) {
        eventTarget.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    getCurrentSelectedOptionValues() {
      const optionInputs = Array.from(this.getCurrentVariantSelects()?.querySelectorAll('.product-form__input') || []);
      return optionInputs.map((inputGroup) => this.getSelectedVariantOptionValue(inputGroup));
    }

    setMainVariantId(variantId) {
      if (!variantId) return;

      const productInfo = document.querySelector('product-info');
      if (productInfo && typeof productInfo.updateVariantInputs === 'function') {
        productInfo.updateVariantInputs(variantId);
        return;
      }

      document
        .querySelectorAll('product-info form input[name="id"], product-form form input[name="id"]')
        .forEach((input) => {
          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    async syncMainVariantFromCurrentOptions() {
      const selectedOptions = this.getCurrentSelectedOptionValues();
      if (!selectedOptions.length) return true;

      const product = await this.getProductJson();
      const variants = product && Array.isArray(product.variants) ? product.variants : [];
      if (!variants.length) return true;

      const variant = variants.find((candidate) => {
        const variantOptions = this.getVariantOptions(candidate);
        return selectedOptions.every((value, index) => !value || variantOptions[index] === value);
      });

      const productInfo = document.querySelector('product-info');
      const productForm = productInfo?.productForm || this.productForm;

      if (!variant) {
        productForm?.toggleSubmitButton?.(true, window.variantStrings?.unavailable);
        this.syncSubmitState();
        return false;
      }

      this.setMainVariantId(variant.id);
      productForm?.toggleSubmitButton?.(!variant.available, window.variantStrings?.soldOut);
      this.syncSubmitState();
      return variant.available;
    }

    async getBestAvailableVariant(optionValues, changedIndex) {
      const product = await this.getProductJson();
      const variants = product && Array.isArray(product.variants) ? product.variants : [];
      if (!variants.length) return null;

      const matchesOptions = (variant, expectedOptions) => {
        const variantOptions = this.getVariantOptions(variant);
        return expectedOptions.every((value, index) => !value || variantOptions[index] === value);
      };
      const exactAvailable = variants.find((variant) => variant.available && matchesOptions(variant, optionValues));
      if (exactAvailable) return exactAvailable;

      const changedValue = optionValues[changedIndex];
      const matchingChangedOption = variants.find((variant) => {
        const variantOptions = this.getVariantOptions(variant);
        return variant.available && variantOptions[changedIndex] === changedValue;
      });
      if (matchingChangedOption) return matchingChangedOption;

      return variants.find((variant) => matchesOptions(variant, optionValues)) || null;
    }

    getVariantOptions(variant) {
      if (Array.isArray(variant.options)) return variant.options;
      return [variant.option1, variant.option2, variant.option3].filter((option) => option != null);
    }

    async getProductJson() {
      if (this.productJson) return this.productJson;
      if (this.productJsonPromise) return this.productJsonPromise;

      const productInfo = document.querySelector('product-info');
      const productUrl = productInfo?.dataset?.url || window.location.pathname;
      this.productJsonPromise = fetch(`${productUrl}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .then((product) => {
          this.productJson = product;
          return product;
        })
        .catch(() => null);

      return this.productJsonPromise;
    }

    syncVariantSummary() {
      this.variantSelects = this.getCurrentVariantSelects();
      if (!this.variantSelects || !this.variantOptionEls || !this.variantOptionEls.length) return;

      const optionInputs = Array.from(this.variantSelects.querySelectorAll('.product-form__input'));
      this.variantOptionEls.forEach((optionEl) => {
        const optionIndex = Number(optionEl.dataset.stickyOptionIndex);
        const inputGroup = optionInputs[optionIndex];
        if (!inputGroup) return;

        const value = this.getSelectedVariantOptionValue(inputGroup);
        const valueEl = optionEl.querySelector('[data-sticky-option-value]');
        const swatchEl = optionEl.querySelector('[data-sticky-summary-swatch]');

        if (valueEl && value) {
          valueEl.textContent = value;
        }
        this.syncStickySwatch(swatchEl, optionEl.dataset.stickyOptionName, value, inputGroup);
      });
    }

    bindModalCloseHandler() {
      // When user closes the protection modal (ESC, X, or finishing a flow),
      // release any busy state on sticky buttons. If the flow continues to
      // addToCart, cartUpdate will arrive shortly after and is a no-op since
      // we're already cleared.
      const protectionPlan = document.querySelector('protection-plan');
      if (!protectionPlan) return;
      const modalId = protectionPlan.dataset && protectionPlan.dataset.modalId;
      if (!modalId) return;
      const modal = document.getElementById(modalId);
      if (!modal) return;

      this.modalCloseHandler = () => {
        if (modal.dataset.protectionSubmitting === 'true') return;
        this.releaseBusyButtons();
      };
      modal.addEventListener('close', this.modalCloseHandler);
      modal.addEventListener('cancel', this.modalCloseHandler);
    }

    bindVisibilityTrigger() {
      const triggerMode = this.dataset.triggerMode || 'after_main_cta';

      if (triggerMode === 'always') {
        this.classList.add('is-visible');
        this.startDynamicCheckoutTracking();
        return;
      }

      this.mainCta = document.querySelector('[id^="ProductSubmitButton-"]');
      if (!this.mainCta) {
        this.classList.add('is-visible');
        this.startDynamicCheckoutTracking();
        return;
      }

      this.isElementPartiallyVisible = (el) => {
        if (!el || !el.isConnected) return false;
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;

        const rect = el.getBoundingClientRect();
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.right > 0 &&
          rect.top < window.innerHeight &&
          rect.left < window.innerWidth
        );
      };

      this.isNativeCheckoutVisible = () => {
        const mainCtaVisible = this.isElementPartiallyVisible(this.mainCta);
        const dynamicCheckoutVisible =
          this.dynamicCheckout &&
          !this.dynamicCheckout.classList.contains('is-sticky-dynamic-checkout') &&
          this.isElementPartiallyVisible(this.dynamicCheckout);

        return mainCtaVisible || dynamicCheckoutVisible;
      };

      this.updateStickyVisibility = () => {
        const shouldShowSticky = !this.isNativeCheckoutVisible();
        const wasVisible = this.classList.contains('is-visible');
        this.classList.toggle('is-visible', shouldShowSticky);
        if (!shouldShowSticky) {
          this.resetDynamicCheckoutPosition();
        } else if (!wasVisible) {
          this.startDynamicCheckoutTracking();
        } else {
          this.syncDynamicCheckoutPosition();
        }
      };
      this.handleStickyVisibilityScroll = debounce(this.updateStickyVisibility, 150);
      this.handleStickyVisibilityResize = debounce(this.updateStickyVisibility, 150);

      this.observer = new IntersectionObserver(
        () => this.updateStickyVisibility(),
        { threshold: 0, rootMargin: '0px' }
      );
      this.observer.observe(this.mainCta);
      if (this.dynamicCheckout) {
        this.observer.observe(this.dynamicCheckout);
      }
      window.addEventListener('scroll', this.handleStickyVisibilityScroll, { passive: true });
      window.addEventListener('resize', this.handleStickyVisibilityResize, { passive: true });
      this.updateStickyVisibility();
    }

    bindPriceSync() {
      if (!this.priceEl) return;

      const mainPriceWrap = document.querySelector('[id^="price-"]');
      if (mainPriceWrap) {
        this.priceObserver = new MutationObserver(() => this.recalculatePrice());
        this.priceObserver.observe(mainPriceWrap, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      this.protectionChangeHandler = (e) => {
        if (e.target && e.target.matches && e.target.matches('.product-protection__radio')) {
          this.recalculatePrice();
        }
      };
      document.addEventListener('change', this.protectionChangeHandler);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.variantPriceUnsub = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          this.recalculatePrice();
        });
      }
    }

    recalculatePrice() {
      if (!this.priceEl) return;

      const mainPriceWrap = document.querySelector('[id^="price-"]');
      const mainPriceRoot = mainPriceWrap?.querySelector('.price');
      const isOnSale = mainPriceRoot?.classList.contains('price--on-sale');
      const mainPriceEl = isOnSale
        ? mainPriceRoot.querySelector('.price__sale .price-item--sale')
        : mainPriceRoot?.querySelector('.price__regular .price-item--regular');
      const comparePriceEl = isOnSale
        ? mainPriceRoot.querySelector('.price__sale .price-item--regular')
        : null;
      const r3MainPriceEl = mainPriceWrap?.querySelector('.section-pdp-01-info-gallery__current-price');
      const r3ComparePriceEl = mainPriceWrap?.querySelector('.section-pdp-01-info-gallery__compare-price');
      const basePriceText = (mainPriceEl || r3MainPriceEl)?.textContent.trim() || '';
      const comparePriceText = (comparePriceEl || r3ComparePriceEl)?.textContent.trim() || '';

      const protectionRadio = document.querySelector('.product-protection__radio:checked');
      const planValue = protectionRadio && protectionRadio.dataset ? protectionRadio.dataset.planValue : '';
      const planLabel = protectionRadio && protectionRadio.dataset ? protectionRadio.dataset.planLabel : '';
      const hasProtection = protectionRadio && protectionRadio.value !== 'decline' && planValue;

      if (this.priceMainEl && basePriceText) this.priceMainEl.textContent = basePriceText;
      if (this.comparePriceEl) {
        this.comparePriceEl.textContent = comparePriceText;
        this.comparePriceEl.hidden = !comparePriceText || comparePriceText === basePriceText;
      }

      if (this.priceAddEl) {
        this.priceAddEl.textContent = hasProtection ? '+ ' + planValue + (planLabel ? ' ' + planLabel : '') : '';
        this.priceAddEl.hidden = !hasProtection;
      }
    }

    setBusy(button, isBusy) {
      if (!button) return;
      button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      button.classList.toggle('is-busy', isBusy);
      this.syncSubmitState();

      // Safety net: if the busy state is never cleared (e.g. modal cancelled
      // before any cartUpdate fires), release it after 8s.
      if (isBusy) {
        if (button._stickyBusyTimer) clearTimeout(button._stickyBusyTimer);
        button._stickyBusyTimer = setTimeout(() => {
          this.setBusy(button, false);
        }, 8000);
      } else if (button._stickyBusyTimer) {
        clearTimeout(button._stickyBusyTimer);
        button._stickyBusyTimer = null;
      }
    }

    releaseBusyButtons() {
      if (this.atcButton) this.setBusy(this.atcButton, false);
    }

    teardown() {
      if (this.observer) this.observer.disconnect();
      if (this.priceObserver) this.priceObserver.disconnect();
      if (this.heightObserver) this.heightObserver.disconnect();
      if (this.submitStateObserver) this.submitStateObserver.disconnect();
      if (this.handleMobileLayoutViewportChange) {
        window.removeEventListener('resize', this.handleMobileLayoutViewportChange);
        window.removeEventListener('orientationchange', this.handleMobileLayoutViewportChange);
        this.handleMobileLayoutViewportChange.cancel();
      }
      if (this.mobileSingleRowMedia && this.mobileLayoutBreakpointHandler) {
        this.mobileSingleRowMedia.removeEventListener?.('change', this.mobileLayoutBreakpointHandler);
      }
      if (this.mobileLayoutFrameId != null) {
        window.cancelAnimationFrame(this.mobileLayoutFrameId);
        this.mobileLayoutFrameId = null;
      }
      this.mobileSingleRowMedia = null;
      this.mobileLayoutBreakpointHandler = null;
      this.handleMobileLayoutViewportChange = null;
      this.mobileLayoutFontReadyToken = null;
      this.classList.remove('sticky-atc--mobile-stacked');
      if (this.handleHeightViewportChange) {
        window.removeEventListener('resize', this.handleHeightViewportChange);
        window.removeEventListener('orientationchange', this.handleHeightViewportChange);
        this.handleHeightViewportChange.cancel();
      }
      if (this.handleStickyVisibilityScroll) {
        window.removeEventListener('scroll', this.handleStickyVisibilityScroll);
        this.handleStickyVisibilityScroll.cancel();
      }
      if (this.handleStickyVisibilityResize) {
        window.removeEventListener('resize', this.handleStickyVisibilityResize);
        this.handleStickyVisibilityResize.cancel();
      }
      if (this.handleDynamicCheckoutViewportChange) {
        window.removeEventListener('resize', this.handleDynamicCheckoutViewportChange);
        window.removeEventListener('orientationchange', this.handleDynamicCheckoutViewportChange);
        this.handleDynamicCheckoutViewportChange.cancel();
      }
      if (this.dynamicCheckoutMedia && this.dynamicCheckoutBreakpointHandler) {
        this.dynamicCheckoutMedia.removeEventListener?.('change', this.dynamicCheckoutBreakpointHandler);
      }
      if (this.dynamicCheckoutTransitionHandler) {
        this.removeEventListener('transitionend', this.dynamicCheckoutTransitionHandler);
        this.removeEventListener('transitioncancel', this.dynamicCheckoutTransitionHandler);
      }
      if (this.dynamicCheckoutOverlayObserver) this.dynamicCheckoutOverlayObserver.disconnect();
      this.resetDynamicCheckoutPosition();
      if (this.cartUpdateUnsub) this.cartUpdateUnsub();
      if (this.cartErrorUnsub) this.cartErrorUnsub();
      if (this.variantPriceUnsub) this.variantPriceUnsub();
      if (this.variantSummaryUnsub) this.variantSummaryUnsub();
      if (this.protectionChangeHandler) {
        document.removeEventListener('change', this.protectionChangeHandler);
      }
      if (this.variantSummaryChangeHandler) {
        document.removeEventListener('change', this.variantSummaryChangeHandler);
      }
      if (this.variantControlsEl && this.stickyVariantControlClickHandler) {
        this.variantControlsEl.removeEventListener('click', this.stickyVariantControlClickHandler);
      }
      if (this.stickyVariantDocumentClickHandler) {
        document.removeEventListener('click', this.stickyVariantDocumentClickHandler);
      }
      if (this.stickyVariantKeydownHandler) {
        document.removeEventListener('keydown', this.stickyVariantKeydownHandler);
      }
      if (this.boundVariantIdInput && this.variantChangeHandler) {
        this.boundVariantIdInput.removeEventListener('change', this.variantChangeHandler);
      }
      if (this.mainQuantityInput && this.mainQuantityChangeHandler) {
        this.mainQuantityInput.removeEventListener('change', this.mainQuantityChangeHandler);
      }
      if (this.quantityControl && this.stickyQuantityClickHandler) {
        this.quantityControl.removeEventListener('click', this.stickyQuantityClickHandler);
      }
      if (this.atcButton && this.atcClickHandler) {
        this.atcButton.removeEventListener('click', this.atcClickHandler);
      }
      if (this.modalCloseHandler) {
        const protectionPlan = document.querySelector('protection-plan');
        const modalId = protectionPlan && protectionPlan.dataset && protectionPlan.dataset.modalId;
        const modal = modalId && document.getElementById(modalId);
        if (modal) {
          modal.removeEventListener('close', this.modalCloseHandler);
          modal.removeEventListener('cancel', this.modalCloseHandler);
        }
      }
    }

    disconnectedCallback() {
      this.teardown();
      if (this.sectionLoadHandler) {
        document.removeEventListener('shopify:section:load', this.sectionLoadHandler);
        this.sectionLoadHandler = null;
      }
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
})();
